import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const chatAPI = createApi({
  reducerPath: 'chat',
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  tagTypes: ['ChatSessions'],
  endpoints: (builder) => ({
    getBorrowerChatHistory: builder.query({
      query: ({ borrowerId, applicationId }) => {
        const params = new URLSearchParams();
        if (borrowerId) params.append('borrower_id', borrowerId);
        if (applicationId) params.append('application_id', applicationId);
        return {
          url: `b2b/ai/chat/application-sessions?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['ChatSessions'],
      transformResponse: (response) => {
        const sessions = Array.isArray(response) ? response : [];
        return {
          chats: sessions.map((s) => ({
            chat_id: s.id,
            title: s.title,
            start_time: s.created_at,
            last_message_at: s.last_message_at,
          })),
        };
      },
    }),
    getChatMessages: builder.query({
      query: ({ chatId, borrowerId, applicationId }) => {
        const params = new URLSearchParams();
        if (borrowerId) params.append('borrower_id', borrowerId);
        if (applicationId) params.append('application_id', applicationId);
        return {
          url: `b2b/ai/chat/application-sessions/${chatId}/messages?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => {
        const messages = Array.isArray(response) ? response : [];
        return {
          messages: messages.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            created_at: m.created_at,
            feedback: m.metadata?.feedback ?? null,
            versions: m.metadata?.versions ?? null,
          })),
        };
      },
    }),

    // ── B2C Chat History endpoints ──────────────────────────────────────────
    getB2cChatSessions: builder.query({
      query: ({ consumerId, clientOrgId }) => {
        const params = new URLSearchParams();
        if (consumerId) params.append('consumer_id', consumerId);
        if (clientOrgId) params.append('client_org_id', clientOrgId);
        return {
          url: `b2c/chat/sessions?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['ChatSessions'],
      transformResponse: (response) => {
        const sessions = Array.isArray(response) ? response : [];
        return {
          chats: sessions.map((s) => ({
            chat_id: s.id,
            title: s.title,
            start_time: s.created_at,
            last_message_at: s.last_message_at,
          })),
        };
      },
    }),
    getB2cChatMessages: builder.query({
      query: ({ sessionId, consumerId, clientOrgId }) => {
        const params = new URLSearchParams();
        if (consumerId) params.append('consumer_id', consumerId);
        if (clientOrgId) params.append('client_org_id', clientOrgId);
        return {
          url: `b2c/chat/sessions/${sessionId}/messages?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => {
        const messages = Array.isArray(response) ? response : [];
        return {
          messages: messages.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            created_at: m.created_at,
            feedback: m.metadata?.feedback ?? null,
            versions: m.metadata?.versions ?? null,
          })),
        };
      },
    }),

    // Set/clear 👍/👎 feedback on an assistant message.
    // B2B authorizes via the session cookie; B2C via consumer_id/client_org_id.
    setMessageFeedback: builder.mutation({
      query: ({ chatId, messageId, rating, isB2C = false, consumerId, clientOrgId }) => {
        if (isB2C) {
          const params = new URLSearchParams();
          if (consumerId) params.append('consumer_id', consumerId);
          if (clientOrgId) params.append('client_org_id', clientOrgId);
          return {
            url: `b2c/chat/sessions/${chatId}/messages/${messageId}/feedback?${params.toString()}`,
            method: 'POST',
            body: { rating },
            headers: getHeaders(),
          };
        }
        return {
          url: `b2b/ai/chat/sessions/${chatId}/messages/${messageId}/feedback`,
          method: 'POST',
          body: { rating },
          headers: getHeaders(),
        };
      },
    }),
  }),
});

export const {
  useGetBorrowerChatHistoryQuery,
  useGetChatMessagesQuery,
  useGetB2cChatSessionsQuery,
  useGetB2cChatMessagesQuery,
  useSetMessageFeedbackMutation,
} = chatAPI;
